import jsPDF from 'jspdf';
import { DESIGN_FEE_TERMS } from '../types';

interface AgreementData {
  clientName: string;
  projectName?: string;
  signedBy: string;
  signedAt: Date;
  signatureDataUrl: string;
  services: { name: string; price: number }[];
  totalAmount: number;
}

export async function generateSignedAgreementPdf(data: AgreementData): Promise<Blob> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let yPos = margin;

  const addPageIfNeeded = (requiredSpace: number) => {
    if (yPos + requiredSpace > pdf.internal.pageSize.getHeight() - 20) {
      pdf.addPage();
      yPos = margin;
    }
  };

  // Header
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(44, 62, 47);
  pdf.text("Leiper's Creek Timber Frames", margin, yPos);
  yPos += 10;

  pdf.setFontSize(14);
  pdf.setTextColor(80, 80, 80);
  pdf.text('Design Services Agreement', margin, yPos);
  yPos += 12;

  // Divider
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.3);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // Client info
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 30, 30);
  pdf.text('Client:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.clientName, margin + 22, yPos);
  yPos += 6;

  if (data.projectName) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Project:', margin, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(data.projectName, margin + 22, yPos);
    yPos += 6;
  }

  pdf.setFont('helvetica', 'bold');
  pdf.text('Date:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(data.signedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), margin + 22, yPos);
  yPos += 12;

  // Order Summary
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(44, 62, 47);
  pdf.text('Selected Design Services', margin, yPos);
  yPos += 8;

  pdf.setFontSize(10);
  pdf.setTextColor(60, 60, 60);

  for (const svc of data.services) {
    addPageIfNeeded(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(svc.name, margin + 2, yPos);
    const priceText = `$${svc.price.toLocaleString()}`;
    pdf.text(priceText, pageWidth - margin - pdf.getTextWidth(priceText), yPos);
    yPos += 6;
  }

  // Total
  yPos += 2;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(30, 30, 30);
  pdf.text('Total', margin + 2, yPos);
  const totalText = `$${data.totalAmount.toLocaleString()}`;
  pdf.text(totalText, pageWidth - margin - pdf.getTextWidth(totalText), yPos);
  yPos += 14;

  // Terms & Conditions
  addPageIfNeeded(20);
  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(44, 62, 47);
  pdf.text('Terms & Conditions', margin, yPos);
  yPos += 8;

  for (const term of DESIGN_FEE_TERMS) {
    addPageIfNeeded(20);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 30, 30);
    pdf.text(term.title, margin + 2, yPos);
    yPos += 5;

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);
    const lines = pdf.splitTextToSize(term.content, contentWidth - 4);
    addPageIfNeeded(lines.length * 4.5 + 4);
    pdf.text(lines, margin + 2, yPos);
    yPos += lines.length * 4.5 + 4;
  }

  // Signature section
  yPos += 6;
  addPageIfNeeded(60);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  pdf.setFontSize(13);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(44, 62, 47);
  pdf.text('Signature', margin, yPos);
  yPos += 8;

  // Signature image
  if (data.signatureDataUrl) {
    try {
      const img = new Image();
      img.src = data.signatureDataUrl;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        setTimeout(resolve, 500);
      });
      pdf.addImage(data.signatureDataUrl, 'PNG', margin, yPos, 60, 25);
      yPos += 30;
    } catch {
      yPos += 5;
    }
  }

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(60, 60, 60);
  pdf.text(`Signed by: ${data.signedBy}`, margin, yPos);
  yPos += 5;
  pdf.text(
    `Date: ${data.signedAt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at ${data.signedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`,
    margin,
    yPos,
  );
  yPos += 10;

  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text(
    'This electronic signature is legally binding and has the same legal effect as a handwritten signature.',
    margin,
    yPos,
  );

  // Footer on last page
  const footerY = pdf.internal.pageSize.getHeight() - 12;
  pdf.setFontSize(8);
  pdf.setTextColor(160, 160, 160);
  pdf.text(`© ${new Date().getFullYear()} Leiper's Creek Timber Frames`, margin, footerY);
  pdf.text('Design Services Agreement', pageWidth - margin - pdf.getTextWidth('Design Services Agreement'), footerY);

  return pdf.output('blob');
}
